import React, { useState, useEffect } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ResumeUpload = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    experience: "",
    resume: null,
  });

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  // =============================
  // Fetch roles (We Hire For)
  // =============================
  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/career/opportunities`);

      const record = Array.isArray(res.data) ? res.data[0] : res.data;
      const raw = record?.we_hire_for || "";

      const parsedRoles = raw
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean);

      setRoles(parsedRoles);
    } catch (error) {
      console.error("Failed to fetch roles", error);
    }
  };

  // =============================
  // Handle text inputs
  // =============================
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear submit status when user starts typing again
    if (submitStatus) setSubmitStatus(null);
  };

  // =============================
  // Handle file selection
  // =============================
  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, resume: e.target.files[0] }));
    
    // Clear submit status when user selects a new file
    if (submitStatus) setSubmitStatus(null);
  };

  // =============================
  // Validation functions
  // =============================
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePhone = (phone) => {
    const regex = /^[0-9]{10,15}$/;
    return regex.test(phone);
  };

  // =============================
  // Submit handler
  // =============================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);

    // Validation
    if (!validateEmail(formData.email)) {
      alert("Please enter a valid email address.");
      return;
    }

    if (!validatePhone(formData.phone)) {
      alert("Please enter a valid phone number (10-15 digits).");
      return;
    }

    if (!formData.resume) {
      alert("Please select a resume file to upload.");
      return;
    }

    setLoading(true);

    try {
      // Step 1: Upload the resume file
      const uploadData = new FormData();
      uploadData.append("media", formData.resume);

      const uploadRes = await axios.post(
        `${API_BASE_URL}/admin/upload-media`,
        uploadData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const fileUrl = uploadRes.data?.data?.files?.[0]?.url;

      if (!fileUrl) {
        alert("❌ File upload failed. Please try again.");
        setLoading(false);
        return;
      }

      // Step 2: Submit the application with resume URL
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        experience: formData.experience,
        resume_data: JSON.stringify({ path: fileUrl }),
      };

      const response = await axios.post(
        `${API_BASE_URL}/career/resume`,
        payload
      );

      // Handle response based on new backend format
      if (response.data.success) {
        setSubmitStatus('success');
        alert("✅ " + response.data.message);
        
        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          role: "",
          experience: "",
          resume: null,
        });
        
        // Reset file input
        e.target.reset();
      } else {
        setSubmitStatus('error');
        alert("⚠️ " + (response.data.message || "Failed to submit application"));
      }
    } catch (error) {
      console.error("❌ Resume submission error:", error.response?.data || error.message);
      setSubmitStatus('error');
      
      const errorMessage = error.response?.data?.error 
        || error.response?.data?.message 
        || "Failed to submit resume. Please try again.";
      
      alert("❌ " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // =============================
  // UI (Responsive Design)
  // =============================
  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-lg sm:shadow-2xl border border-gray-100 space-y-4 sm:space-y-5 w-full max-w-lg mx-auto lg:mx-0"
    >
      <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-3 sm:mb-4">
        Submit Your Application
      </h3>

      {/* Success/Error Message */}
      {submitStatus === 'success' && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
          ✅ Application submitted successfully! We'll review your resume and get back to you soon.
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          ❌ There was an error submitting your application. Please try again.
        </div>
      )}

      {/* Name */}
      <div>
        <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-1 sm:mb-2">
          Full Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          className="w-full p-2.5 sm:p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm sm:text-base transition-all"
          placeholder="Enter your full name"
          required
          disabled={loading}
        />
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-1 sm:mb-2">
          Email Address <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          className="w-full p-2.5 sm:p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm sm:text-base transition-all"
          placeholder="your.email@example.com"
          required
          disabled={loading}
        />
      </div>

      {/* Phone */}
      <div>
        <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-1 sm:mb-2">
          Phone Number <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleInputChange}
          className="w-full p-2.5 sm:p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm sm:text-base transition-all"
          placeholder="10 digit phone number"
          required
          disabled={loading}
          maxLength="15"
        />
        <p className="text-xs text-gray-500 mt-1">Enter 10 digits without spaces or special characters</p>
      </div>

      {/* Role Dropdown */}
      <div>
        <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-1 sm:mb-2">
          Position Applied For <span className="text-red-500">*</span>
        </label>
        <select
          name="role"
          value={formData.role}
          onChange={handleInputChange}
          required
          disabled={loading}
          className="w-full p-2.5 sm:p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm sm:text-base bg-white transition-all"
        >
          <option value="">Select a role</option>
          {roles.map((role, index) => (
            <option key={index} value={role}>
              {role}
            </option>
          ))}
        </select>
      </div>

      {/* Experience */}
      <div>
        <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-1 sm:mb-2">
          Years of Experience <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          name="experience"
          value={formData.experience}
          onChange={handleInputChange}
          className="w-full p-2.5 sm:p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm sm:text-base transition-all"
          placeholder="e.g., 5"
          required
          disabled={loading}
          min="0"
          max="50"
        />
      </div>

      {/* Resume Upload */}
      <div>
        <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-1 sm:mb-2">
          Upload Resume <span className="text-red-500">*</span>
        </label>
        <input
          type="file"
          name="resume"
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx"
          className="w-full p-2.5 sm:p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm sm:text-base file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-all"
          required
          disabled={loading}
        />
        <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, DOC, DOCX (Max 10MB)</p>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading}
        className={`w-full ${
          loading
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
        } text-white py-3 sm:py-4 rounded-lg font-semibold sm:font-bold text-sm sm:text-lg shadow-md sm:shadow-lg transition-all transform ${
          !loading && "hover:scale-[1.02]"
        }`}
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Submitting Application...
          </span>
        ) : (
          "Submit Application"
        )}
      </button>

      {/* Privacy Note */}
      <p className="text-xs text-gray-500 text-center mt-2">
        By submitting this form, you agree to our privacy policy. We will only use your information for recruitment purposes.
      </p>
    </form>
  );
};

export default ResumeUpload;